import * as ents from '@attain-apps/entities'

/** Concurrent execution state for connector runs in one zone. */
export interface State {
  /** Whether the future DMS run launcher is open. */
  readonly isLauncherOpen: boolean
  /** Latest execution state keyed by connector ID. */
  readonly entries: Readonly<
    Partial<Record<ents.core.integrations.Connector.Id, State.Entry>>
  >
  /** Models selected for truncation, grouped by connector ID. */
  readonly truncationSelections: Readonly<
    Partial<
      Record<
        ents.core.integrations.Connector.Id,
        Readonly<Partial<Record<ents.core.dms.models.ForRecord.Id, true>>>
      >
    >
  >
}
export namespace State {
  /** One connector's latest execution state. */
  export interface Entry {
    readonly data?: true
    readonly error?: Error
    readonly loading?: true
    readonly run?: ents.core.integrations.Connector.Run
  }

  export const INITIAL: Readonly<State> = {
    entries: {},
    isLauncherOpen: false,
    truncationSelections: {},
  }

  /** Opens the launcher or closes it and clears destructive selections. */
  export function withLauncher(state: State, isLauncherOpen: boolean): State {
    return {
      ...state,
      isLauncherOpen,
      truncationSelections: isLauncherOpen ? state.truncationSelections : {},
    }
  }

  /** Sets one connector's latest execution entry. */
  export function withEntry(
    state: State,
    connectorId: ents.core.integrations.Connector.Id,
    entry: Entry,
  ): State {
    return {
      ...state,
      entries: { ...state.entries, [connectorId]: entry },
    }
  }

  /** Removes a settled entry; active runs cannot be dismissed. */
  export function withoutEntry(
    state: State,
    connectorId: ents.core.integrations.Connector.Id,
  ): State {
    if (state.entries[connectorId]?.loading) {
      return state
    }
    const entries = { ...state.entries }
    delete entries[connectorId]
    return { ...state, entries }
  }

  /** Selects or deselects one target model for truncation. */
  export function withTruncationSelection(
    state: State,
    connectorId: ents.core.integrations.Connector.Id,
    modelId: ents.core.dms.models.ForRecord.Id,
    selected: boolean,
  ): State {
    const connectorSelections = {
      ...state.truncationSelections[connectorId],
    }
    if (selected) {
      connectorSelections[modelId] = true
    } else {
      delete connectorSelections[modelId]
    }
    return {
      ...state,
      truncationSelections: {
        ...state.truncationSelections,
        [connectorId]: connectorSelections,
      },
    }
  }

  /** Clears every model selection for one connector. */
  export function withoutTruncationSelections(
    state: State,
    connectorId: ents.core.integrations.Connector.Id,
  ): State {
    const truncationSelections = { ...state.truncationSelections }
    delete truncationSelections[connectorId]
    return { ...state, truncationSelections }
  }

  /** Applies progress streamed for one connector and clears settled selections. */
  export function withRunProgress(
    state: State,
    connectorId: ents.core.integrations.Connector.Id,
    run?: ents.core.integrations.Connector.Run,
  ): State {
    const isRunning =
      run !== undefined &&
      !run.error &&
      ents.core.integrations.Connector.Run.Status.fromImports(run.imports) ===
        ents.core.integrations.Connector.Run.Status.Running
    const nextState = withEntry(state, connectorId, {
      loading: isRunning ? true : undefined,
      run,
    })
    return run && !isRunning
      ? withoutTruncationSelections(nextState, connectorId)
      : nextState
  }

  /** Marks one connector run as requested. */
  export function withRunRequest(
    state: State,
    connectorId: ents.core.integrations.Connector.Id,
  ): State {
    return withEntry(state, connectorId, { loading: true })
  }

  /** Records one failed request and clears its destructive selections. */
  export function withRunError(
    state: State,
    connectorId: ents.core.integrations.Connector.Id,
    error: Error,
  ): State {
    return withoutTruncationSelections(
      withEntry(state, connectorId, { error }),
      connectorId,
    )
  }

  /** Records one completed request and clears its destructive selections. */
  export function withRunSuccess(
    state: State,
    connectorId: ents.core.integrations.Connector.Id,
  ): State {
    return withoutTruncationSelections(
      withEntry(state, connectorId, { data: true }),
      connectorId,
    )
  }

  /** Removes one cancelled request and clears its destructive selections. */
  export function withoutRunRequest(
    state: State,
    connectorId: ents.core.integrations.Connector.Id,
  ): State {
    return withoutTruncationSelections(
      withoutEntry(state, connectorId),
      connectorId,
    )
  }
}
